import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Package,
    Plus,
    X,
    Loader2,
    ChevronDown,
    Code2,
    ShoppingCart,
    GraduationCap,
    HeartPulse,
    Landmark,
    Factory,
    Store,
    Plane,
    Car,
    Gavel,
    Megaphone,
    Building2,
    Pencil,
    MoreVertical,
    BarChart3,
    ArrowUpRight,
    ArrowLeft,
} from "lucide-react";
import Starfield from "./Starfield";
import Sidebar from "./company_Dashboard_sidebar";
import { GROWTHOS_BASE, authHeaders } from "../config/api";

/* ==========================================================
   PRODUCTS PAGE — full product list + add/edit CRUD, split
   out of Home.jsx so product management has its own route
   (sidebar "Products" link points here). Same
   isfathena.products API and fields as before.
========================================================== */

//const API_BASE_URL = "http://localhost:8000"; //do comment for build
const PRODUCTS_ENDPOINT = `${GROWTHOS_BASE}/products`;

const formatPrice = (num) => {
    const n = Number(num) || 0;
    return `₹${n.toLocaleString("en-IN")}`;
};

const OTHER = "Other (specify)";

const BILLING_CYCLE_OPTIONS = ["Monthly", "Quarterly", "Yearly"];

const CATEGORY_OPTIONS = [
    "Real Estate",
    "SaaS / Software",
    "E-commerce",
    "Education",
    "Healthcare",
    "Finance & Banking",
    "Manufacturing",
    "Retail",
    "Hospitality & Travel",
    "Automotive",
    "Legal Services",
    "Marketing & Advertising",
    OTHER,
];

const CATEGORY_ICONS = {
    "Real Estate": Building2,
    "SaaS / Software": Code2,
    "E-commerce": ShoppingCart,
    Education: GraduationCap,
    Healthcare: HeartPulse,
    "Finance & Banking": Landmark,
    Manufacturing: Factory,
    Retail: Store,
    "Hospitality & Travel": Plane,
    Automotive: Car,
    "Legal Services": Gavel,
    "Marketing & Advertising": Megaphone,
};
const categoryIcon = (category) => CATEGORY_ICONS[category] || Package;

const LOCATION_OPTIONS = [
    "Mumbai, India",
    "Delhi NCR, India",
    "Bangalore, India",
    "Pune, India",
    "Hyderabad, India",
    "Chennai, India",
    "Ahmedabad, India",
    "Nashik, India",
    "Pan India",
    "International",
    OTHER,
];

const KEYWORD_OPTIONS = [
    "CRM software",
    "Sales automation",
    "Lead management",
    "Marketing automation",
    "WhatsApp marketing",
    "Email marketing",
    "Digital marketing services",
    "Business consulting",
    OTHER,
];

const AUDIENCE_OPTIONS = [
    "Small business owners",
    "Startups",
    "Enterprise companies",
    "Individual consumers",
    "Students",
    "Working professionals",
    "Freelancers",
    "Government / Public sector",
    "Non-profit organizations",
    OTHER,
];

const emptyForm = {
    product_name: "",
    description: "",
    price: "",
    billing_cycle: "",
    image_url: "",
    category: [],
    target_keywords: [],
    target_location: [],
    target_audience: [],
};

// Backend/DB still stores these as a single comma-separated string (that
// was already true for target_keywords — the old placeholder even showed
// "crm software, sales automation, lead management" as one string). Multi
// -select just makes picking several of those easier; the wire format is
// unchanged.
const parseMulti = (str) =>
    (str || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

// Splits a stored comma-separated string into { selected, customText } for
// a given field's predefined OPTIONS list — predefined values become
// checked checkboxes, anything else becomes the "Other (specify)" free text.
const splitKnownCustom = (str, knownOptions) => {
    const values = parseMulti(str);
    const known = values.filter((v) => v !== OTHER && knownOptions.includes(v));
    const custom = values.filter((v) => v !== OTHER && !knownOptions.includes(v));
    return { selected: custom.length ? [...known, OTHER] : known, customText: custom.join(", ") };
};

/* ---------- presentational pieces ---------- */

const ProductCard = ({ product, onEdit, onManage, onAnalytics }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const CatIcon = categoryIcon(product.category);
    const billingCycle = product.billing_cycle || "Monthly";
    const status = product.status || "Active";

    return (
        <div
            onClick={onManage}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onManage?.();
            }}
            className="group relative flex cursor-pointer flex-col overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:border-orange-500/50 hover:shadow-[0_0_0_1px_rgba(255,107,0,0.25),0_20px_50px_-20px_rgba(255,107,0,0.35)]"
        >
            <div className="relative h-36 w-full shrink-0 overflow-hidden bg-white/[0.03]">
                {product.image_url ? (
                    <img
                        src={product.image_url}
                        alt={product.product_name}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        <CatIcon size={30} className="text-zinc-700" />
                    </div>
                )}

                {product.category && (
                    <span className="absolute left-3 top-3 rounded-full border border-orange-500/30 bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-orange-300 backdrop-blur-sm">
                        {product.category}
                    </span>
                )}

                <div ref={menuRef} className="absolute right-2.5 top-2.5">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen((v) => !v);
                        }}
                        aria-label="Product options"
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-black/70 text-zinc-300 backdrop-blur-sm transition hover:border-orange-500/50 hover:text-white"
                    >
                        <MoreVertical size={14} />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 z-20 mt-1.5 w-40 overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a] py-1 shadow-xl">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen(false);
                                    onEdit?.(e);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-white/5 hover:text-white"
                            >
                                <Pencil size={12} /> Edit product
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen(false);
                                    onAnalytics?.(product);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-white/5 hover:text-white"
                            >
                                <BarChart3 size={12} /> View analytics
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="truncate text-[15px] font-semibold text-white">{product.product_name}</h3>
                    <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            status === "Active"
                                ? "border border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                                : "border border-zinc-500/25 bg-zinc-500/10 text-zinc-400"
                        }`}
                    >
                        {status}
                    </span>
                </div>

                {product.description && (
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-zinc-500">{product.description}</p>
                )}

                <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="text-lg font-extrabold text-orange-400">{formatPrice(product.price)}</span>
                    <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-medium text-zinc-500">
                        /{{ yearly: "year", quarterly: "quarter" }[billingCycle.toLowerCase()] || "month"}
                    </span>
                </div>

                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onManage?.();
                        }}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 py-2 text-xs font-semibold text-zinc-200 transition hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-300"
                    >
                        Manage
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAnalytics?.(product);
                        }}
                        aria-label="View analytics"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-zinc-300 transition hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-300"
                    >
                        <ArrowUpRight size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Multi-select dropdown: click to open a checkbox list, selected values show
// as removable chips on the trigger. Selecting "Other (specify)" reveals a
// free-text input below (comma-separate multiple custom entries there).
//
// The panel renders through a portal into document.body instead of as a
// normal absolutely-positioned child. The trigger sits inside the Add/Edit
// Product modal's own scrolling body (overflow-y-auto) — a plain absolute
// child gets clipped by that scroll box whenever the field is anywhere
// near the bottom, which is what forced the extra manual scrolling. The
// portal escapes that clipping and is positioned from the trigger's live
// on-screen position instead, flipping upward automatically if there
// isn't room below (same as a native <select>).
const MultiSelectField = ({ label, required, options, selected, onToggle, customText, onCustomTextChange, customPlaceholder }) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const computeRect = () => {
        const btn = triggerRef.current;
        if (!btn) return;
        const box = btn.getBoundingClientRect();
        const PANEL_MAX_HEIGHT = 288; // keep in sync with the panel's max-h below
        const spaceBelow = window.innerHeight - box.bottom;
        const openUp = spaceBelow < PANEL_MAX_HEIGHT && box.top > spaceBelow;
        setRect({
            left: box.left,
            width: box.width,
            top: openUp ? undefined : box.bottom + 6,
            bottom: openUp ? window.innerHeight - box.top + 6 : undefined,
            maxHeight: Math.min(PANEL_MAX_HEIGHT, openUp ? box.top - 12 : spaceBelow - 12),
        });
    };

    useEffect(() => {
        if (!open) return;
        computeRect();
        window.addEventListener("scroll", computeRect, true);
        window.addEventListener("resize", computeRect);
        return () => {
            window.removeEventListener("scroll", computeRect, true);
            window.removeEventListener("resize", computeRect);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (
                triggerRef.current?.contains(e.target) ||
                panelRef.current?.contains(e.target)
            ) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    return (
        <div className="relative">
            <style>{`
                .ms-dropdown-scroll { scrollbar-width: thin; scrollbar-color: rgba(255, 107, 0, 0.35) transparent; }
                .ms-dropdown-scroll::-webkit-scrollbar { width: 6px; }
                .ms-dropdown-scroll::-webkit-scrollbar-track { background: transparent; }
                .ms-dropdown-scroll::-webkit-scrollbar-thumb { background-color: rgba(255, 107, 0, 0.35); border-radius: 9999px; }
                .ms-dropdown-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(255, 107, 0, 0.55); }
            `}</style>

            <label className="mb-1.5 block text-xs font-semibold text-zinc-400">
                {label} {required && <span className="text-orange-500">*</span>}
            </label>

            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border bg-white/5 px-3 py-2 text-left text-sm outline-none transition ${
                    open ? "border-orange-600/60" : "border-white/10 focus:border-orange-600/50"
                }`}
            >
                {selected.length === 0 ? (
                    <span className="text-zinc-500">Select...</span>
                ) : (
                    selected.map((val) => (
                        <span
                            key={val}
                            className="flex items-center gap-1 rounded-full bg-orange-600/20 px-2 py-0.5 text-xs font-medium text-orange-300"
                        >
                            {val}
                            <X
                                size={10}
                                className="cursor-pointer text-orange-300/70 hover:text-orange-200"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(val);
                                }}
                            />
                        </span>
                    ))
                )}
                <ChevronDown size={14} className={`ml-auto shrink-0 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && rect && createPortal(
                <div
                    ref={panelRef}
                    style={{
                        position: "fixed",
                        left: rect.left,
                        width: rect.width,
                        top: rect.top,
                        bottom: rect.bottom,
                        maxHeight: rect.maxHeight,
                    }}
                    className="ms-dropdown-scroll z-[100] overflow-y-auto rounded-xl border border-orange-600/20 bg-[#0d0d0d] p-1.5 shadow-2xl"
                >
                    {options.map((opt) => {
                        const checked = selected.includes(opt);
                        return (
                            <label
                                key={opt}
                                className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition ${
                                    checked ? "bg-orange-600/10 text-orange-200" : "text-zinc-300 hover:bg-white/5"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => onToggle(opt)}
                                    className="h-4 w-4 shrink-0 accent-orange-500"
                                />
                                {opt}
                            </label>
                        );
                    })}
                </div>,
                document.body
            )}

            {selected.includes(OTHER) && (
                <input
                    type="text"
                    value={customText}
                    onChange={onCustomTextChange}
                    placeholder={customPlaceholder || "Type your own — comma-separate for multiple"}
                    autoFocus
                    className="mt-2 w-full rounded-lg border border-orange-600/40 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition"
                />
            )}
        </div>
    );
};

/* ---------------------------- main component ---------------------------- */

const Products = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [customFields, setCustomFields] = useState({
        category: "",
        target_location: "",
        target_keywords: "",
        target_audience: "",
    });
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchProducts = async () => {
        setProductsLoading(true);
        try {
            const res = await fetch(PRODUCTS_ENDPOINT, { method: "GET", headers: authHeaders() });
            if (res.status === 401) {
                navigate("/login");
                return;
            }
            const resp = await res.json();
            setProducts(res.ok && resp.success && Array.isArray(resp.data) ? resp.data : []);
        } catch (err) {
            setProducts([]);
        } finally {
            setProductsLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem("growthos_token")) {
            navigate("/login");
            return;
        }
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If we arrived here via ?add=1 (e.g. a "Add Product" quick action
    // elsewhere in the app), open the modal automatically.
    useEffect(() => {
        if (searchParams.get("add") === "1") openAddModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const toggleOption = (field) => (value) =>
        setForm((f) => {
            const arr = f[field];
            const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
            return { ...f, [field]: next };
        });

    const updateCustomText = (field) => (e) =>
        setCustomFields((c) => ({ ...c, [field]: e.target.value }));

    // Turns a field's selected checkboxes + custom text into the single
    // comma-separated string the backend/DB expects for that column.
    const finalizeMulti = (field) => {
        const picked = form[field].filter((v) => v !== OTHER);
        const custom = parseMulti(customFields[field]);
        return [...picked, ...custom].join(", ");
    };

    const isMultiFieldFilled = (field) =>
        form[field].some((v) => v !== OTHER) || parseMulti(customFields[field]).length > 0;

    const openAddModal = () => {
        setEditingProduct(null);
        setForm(emptyForm);
        setCustomFields({ category: "", target_location: "", target_keywords: "", target_audience: "" });
        setFormError("");
        setModalOpen(true);
    };

    const openEditModal = (p) => (e) => {
        e?.stopPropagation();
        const cat = splitKnownCustom(p.category, CATEGORY_OPTIONS);
        const loc = splitKnownCustom(p.target_location, LOCATION_OPTIONS);
        const kw = splitKnownCustom(p.target_keywords, KEYWORD_OPTIONS);
        const aud = splitKnownCustom(p.target_audience, AUDIENCE_OPTIONS);
        setEditingProduct(p);
        setForm({
            product_name: p.product_name || "",
            description: p.description || "",
            price: p.price != null ? String(p.price) : "",
            billing_cycle: p.billing_cycle || "",
            image_url: p.image_url || "",
            category: cat.selected,
            target_keywords: kw.selected,
            target_location: loc.selected,
            target_audience: aud.selected,
        });
        setCustomFields({
            category: cat.customText,
            target_location: loc.customText,
            target_keywords: kw.customText,
            target_audience: aud.customText,
        });
        setFormError("");
        setModalOpen(true);
    };

    const handleSubmitProduct = async (e) => {
        e.preventDefault();
        if (!form.product_name.trim()) return setFormError("Product name is required.");
        if (!form.description.trim()) return setFormError("Description is required.");
        if (!form.price || Number(form.price) <= 0) return setFormError("Enter a valid price.");
        if (!form.billing_cycle) return setFormError("Billing Cycle is required.");
        if (!isMultiFieldFilled("category")) return setFormError("Category / Industry is required.");
        if (!isMultiFieldFilled("target_location")) return setFormError("Target Location is required.");
        if (!isMultiFieldFilled("target_keywords")) return setFormError("Target Keywords is required.");
        if (!isMultiFieldFilled("target_audience")) return setFormError("Target Audience is required.");

        setSaving(true);
        setFormError("");
        try {
            const isEditing = !!editingProduct;
            const res = await fetch(
                isEditing ? `${PRODUCTS_ENDPOINT}/${editingProduct.id}` : PRODUCTS_ENDPOINT,
                {
                    method: isEditing ? "PATCH" : "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({
                        product_name: form.product_name.trim(),
                        description: form.description.trim(),
                        price: Number(form.price) || 0,
                        billing_cycle: form.billing_cycle,
                        image_url: form.image_url.trim(),
                        category: finalizeMulti("category"),
                        target_keywords: finalizeMulti("target_keywords"),
                        target_location: finalizeMulti("target_location"),
                        target_audience: finalizeMulti("target_audience"),
                    }),
                }
            );
            const resp = await res.json().catch(() => null);
            if (!res.ok || !resp?.success) {
                console.error("Product save failed:", res.status, resp);
                throw new Error(resp?.message || `Request failed (${res.status})`);
            }
            setModalOpen(false);
            setEditingProduct(null);
            setForm(emptyForm);
            fetchProducts();
        } catch (err) {
            console.error("Product save error:", err);
            setFormError(err.message || `Could not ${editingProduct ? "update" : "save"} this product. Please try again.`);
        } finally {
            setSaving(false);
        }
    };

    const openDashboard = (p) => {
        localStorage.setItem("growthos_active_product_id", String(p.id));
        navigate(`/app/dashboard-preview?product=${p.id}`);
    };

    const openAnalytics = (p) => {
        navigate(`/analytics?product=${p.id}`);
    };

    return (
        <div className="relative min-h-screen w-full bg-[#050505] text-zinc-300">
            <Sidebar />

            <div className="relative min-h-screen w-full overflow-hidden lg:pl-64">
                <Starfield />

                <div className="relative z-10 w-full space-y-8 px-4 py-10 sm:px-6 sm:py-14 lg:px-10">
                    <div className="-mb-4">
                        <button
                            type="button"
                            onClick={() => navigate("/company_home")}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-gray-300 transition hover:border-orange-600/40 hover:bg-orange-600/10 hover:text-orange-400"
                        >
                            <ArrowLeft size={15} /> Back
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h1 className="flex items-center gap-2 text-2xl font-extrabold text-white sm:text-3xl">
                                <Package size={22} className="text-orange-500" /> Products
                            </h1>
                            <p className="mt-1 text-sm text-zinc-500">Manage and monitor all your products</p>
                        </div>
                        <button
                            type="button"
                            onClick={openAddModal}
                            className="flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500"
                        >
                            <Plus size={16} /> Add Product
                        </button>
                    </div>

                    {productsLoading ? (
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 size={14} className="animate-spin" /> Loading products...
                        </div>
                    ) : products.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 rounded-[24px] border border-dashed border-orange-600/30 py-14">
                            <Package size={28} className="text-zinc-600" />
                            <p className="text-sm text-zinc-500">No products yet — click "Add Product" to add your first one.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
                            {products.map((p) => (
                                <ProductCard
                                    key={p.id}
                                    product={p}
                                    onEdit={openEditModal(p)}
                                    onManage={() => openDashboard(p)}
                                    onAnalytics={openAnalytics}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Product modal */}
            {modalOpen && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto bg-black/70 px-4 py-8 backdrop-blur-sm"
                    onClick={() => { setModalOpen(false); setEditingProduct(null); }}
                >
                    <div
                        className="relative mx-auto flex w-full max-w-4xl flex-col rounded-2xl border border-orange-600/30 bg-[#0a0a0a] p-4 sm:p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => { setModalOpen(false); setEditingProduct(null); }}
                            className="absolute right-5 top-5 text-zinc-500 transition hover:text-white"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>

                        <h2 className="shrink-0 text-xl font-bold text-white">{editingProduct ? "Edit Product" : "Add Product"}</h2>

                        <form onSubmit={handleSubmitProduct} className="mt-5 flex flex-col">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
                                {/* Left column — basic info */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-zinc-400">Product Name <span className="text-orange-500">*</span></label>
                                        <input
                                            type="text"
                                            value={form.product_name}
                                            onChange={update("product_name")}
                                            placeholder="e.g. GrowthOS AI CRM"
                                            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition focus:border-orange-600/50"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-zinc-400">Description <span className="text-orange-500">*</span></label>
                                        <textarea
                                            value={form.description}
                                            onChange={update("description")}
                                            rows={4}
                                            placeholder="Short description..."
                                            className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition focus:border-orange-600/50"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-zinc-400">Price (₹) <span className="text-orange-500">*</span></label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={form.price}
                                                onChange={update("price")}
                                                placeholder="0"
                                                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition focus:border-orange-600/50"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-zinc-400">Billing Cycle <span className="text-orange-500">*</span></label>
                                            <div className="relative">
                                                <select
                                                    value={form.billing_cycle}
                                                    onChange={update("billing_cycle")}
                                                    className="w-full cursor-pointer appearance-none rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 pr-8 text-sm text-white outline-none transition focus:border-orange-600/50"
                                                >
                                                    <option value="" className="bg-[#0a0a0a]">Select...</option>
                                                    {BILLING_CYCLE_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt} className="bg-[#0a0a0a]">{opt}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-zinc-400">Image URL <span className="text-zinc-600">(optional)</span></label>
                                        <input
                                            type="text"
                                            value={form.image_url}
                                            onChange={update("image_url")}
                                            placeholder="https://..."
                                            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition focus:border-orange-600/50"
                                        />
                                    </div>
                                </div>

                                {/* Right column — lead targeting */}
                                <div className="space-y-4 border-t border-white/10 pt-4 md:border-l md:border-t-0 md:pl-8 md:pt-0">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-orange-400">Lead Targeting</p>
                                        <p className="mt-1 text-[11px] text-zinc-500">
                                            Used later to find leads for this product from an external source — that integration isn't
                                            connected yet, this just saves your targeting info for when it is.
                                        </p>
                                    </div>

                                    <MultiSelectField
                                        label="Category / Industry"
                                        required
                                        options={CATEGORY_OPTIONS}
                                        selected={form.category}
                                        onToggle={toggleOption("category")}
                                        customText={customFields.category}
                                        onCustomTextChange={updateCustomText("category")}
                                        customPlaceholder="Type your industry — comma-separate for multiple"
                                    />

                                    <MultiSelectField
                                        label="Target Location"
                                        required
                                        options={LOCATION_OPTIONS}
                                        selected={form.target_location}
                                        onToggle={toggleOption("target_location")}
                                        customText={customFields.target_location}
                                        onCustomTextChange={updateCustomText("target_location")}
                                        customPlaceholder="Type a location — comma-separate for multiple"
                                    />

                                    <MultiSelectField
                                        label="Target Keywords"
                                        required
                                        options={KEYWORD_OPTIONS}
                                        selected={form.target_keywords}
                                        onToggle={toggleOption("target_keywords")}
                                        customText={customFields.target_keywords}
                                        onCustomTextChange={updateCustomText("target_keywords")}
                                        customPlaceholder="e.g. crm software, sales automation, lead management"
                                    />

                                    <MultiSelectField
                                        label="Target Audience"
                                        required
                                        options={AUDIENCE_OPTIONS}
                                        selected={form.target_audience}
                                        onToggle={toggleOption("target_audience")}
                                        customText={customFields.target_audience}
                                        onCustomTextChange={updateCustomText("target_audience")}
                                        customPlaceholder="Type your target audience — comma-separate for multiple"
                                    />
                                </div>
                            </div>

                            {formError && <p className="mt-4 shrink-0 text-xs text-red-400">{formError}</p>}

                            <div className="flex shrink-0 justify-end gap-3 border-t border-white/10 pt-4 mt-6">
                                <button
                                    type="button"
                                    onClick={() => { setModalOpen(false); setEditingProduct(null); }}
                                    disabled={saving}
                                    className="rounded-lg px-4 py-2.5 text-sm font-semibold text-zinc-300 transition hover:bg-white/5 hover:text-white disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60"
                                >
                                    {saving ? "Saving..." : editingProduct ? "Save Changes" : "Add Product"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;